import type { DayView } from '@tabel/shared/schemas/rules';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookmarkPlus } from 'lucide-react';
import { useId, useState } from 'react';
import { useToast } from '../../components/ErrorToast';
import { Button, Skeleton, inputCls } from '../../components/ui';
import { api, rulesKeys } from '../../lib/api';
import { ApiError, isTransient, reportClientError } from '../../lib/errors';
import { useRole } from '../../lib/role';
import { type OpenDay, PassBar, passesOf, resultText } from './ExampleDay';
import { DialogFooter, GuardedModal, type ModalControl, useDirty } from './GuardedModal';
import { shouldReport } from './LoadError';
import { LABEL, T, say } from './texts';

/*
 * Окно «Сделать проверочным примером» (004, US5, T095; contracts/ui.md, «Окна»; FR-051; сценарии US5 1, 2):
 * - день: человек, дата, проходы полосой и строкой;
 * - ожидаемый результат — расчёт дня по правилам, которые действуют на эту дату (`POST /api/rules/day` без правки);
 *   человек в примере — псевдоним;
 * - «Название» 3–120 знаков, без ФИО и табельных номеров. Проверяет сервер (`400 VALIDATION`): ошибка встаёт у поля
 *   вместо подсказки, название остаётся (FR-061).
 * Пример хранит копию входа дня — новые проходы реального дня его не меняют (FR-053): это делает сервер.
 */

const TITLE_MIN = 3;
const TITLE_MAX = 120;

function PinBody({
  day,
  view,
  tzOffset,
  ctl,
  onDone,
}: {
  day: OpenDay;
  view?: DayView;
  tzOffset: string;
  ctl: ModalControl;
  onDone: () => void;
}) {
  const ids = useId();
  const { role } = useRole();
  const qc = useQueryClient();
  const { showError, showInfo } = useToast();
  const [title, setTitle] = useState('');
  /** Сервер не принял название (ФИО или номер человека объекта) — до следующей правки названия. */
  const [personal, setPersonal] = useState(false);
  useDirty(ctl, title.trim() !== '');

  // День «Сейчас» — по действующим правилам: тот же ключ, что у примера без правки.
  const calc = useQuery({
    queryKey: rulesKeys.day(role, day.personId, day.date, ''),
    queryFn: async () => {
      try {
        return await api.rulesDay({ personId: day.personId, date: day.date });
      } catch (e) {
        if (shouldReport(e)) reportClientError('Rules', 'pin day', e);
        throw e;
      }
    },
    enabled: view === undefined,
  });
  const now = view ?? calc.data?.now;
  const passes = now ? passesOf(now, day.date, tzOffset) : null;
  const dayErrorId = calc.error instanceof ApiError ? calc.error.requestId : null;

  const pin = useMutation({
    mutationFn: () => api.pinGolden({ personId: day.personId, date: day.date, title: title.trim() }),
    onSuccess: () => {
      showInfo(T.pinned);
      void qc.invalidateQueries({ queryKey: rulesKeys.goldens(role) });
      onDone();
    },
    onError: (e) => {
      // Название с ФИО или номером человека (002 FR-060): ошибка — у поля.
      if (e instanceof ApiError && e.code === 'VALIDATION') {
        setPersonal(true);
        return;
      }
      showError(e, {
        screen: 'Rules',
        action: 'pin golden',
        retry: isTransient(e) ? () => pin.mutate() : undefined,
      });
    },
  });

  const length = title.trim().length;
  const reason =
    length < TITLE_MIN || length > TITLE_MAX ? T.whyTitle : personal ? say.fixField(LABEL.goldenTitle) : null;

  return (
    <div className="space-y-4 text-[13.5px]">
      <div className="space-y-1.5 rounded-[10px] border border-line bg-subtle px-3 py-2.5">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-ink-2">
          <b className="font-semibold text-ink">{say.exampleWho(day.name, day.date)}</b>
          {passes && (
            <>
              <span aria-hidden>·</span>
              <span className="tabular-nums">{passes.text}</span>
            </>
          )}
        </p>
        {passes && <PassBar marks={passes.marks} spans={passes.spans} />}
        <div>
          <p className="text-[12px] font-medium text-ink-2">{LABEL.expected}</p>
          {now ? (
            <p className="text-[15px] font-semibold text-ink tabular-nums">{resultText(now)}</p>
          ) : calc.error ? (
            <p className="text-bad">
              {T.dayError}
              {dayErrorId && (
                <span className="block text-[12px] text-ink-3">{say.errorCode(dayErrorId)}</span>
              )}
            </p>
          ) : (
            <div aria-busy="true" aria-label={LABEL.loading} className="py-1">
              <Skeleton className="h-4 w-28" />
            </div>
          )}
          {now?.why && <p className="text-[13px] break-words text-ink-2">{now.why}</p>}
        </div>
        <p className="text-[12.5px] text-ink-2">{T.pinExpected}</p>
        <p className="text-[12.5px] text-ink-2">{T.pinPseudonym}</p>
      </div>

      <div className="space-y-1.5">
        <label htmlFor={`${ids}-title`} className="block font-medium text-ink">
          {LABEL.goldenTitle}
        </label>
        <input
          id={`${ids}-title`}
          type="text"
          data-autofocus
          autoComplete="off"
          maxLength={TITLE_MAX}
          value={title}
          placeholder={T.pinPlaceholder}
          aria-invalid={personal || undefined}
          aria-describedby={`${ids}-title-note`}
          onChange={(e) => {
            setTitle(e.target.value);
            setPersonal(false);
          }}
          className={`${inputCls} ${personal ? 'border-bad' : ''}`}
        />
        {/* Ошибка заменяет подсказку: раскладка не прыгает (дизайн-система, «Формы»). */}
        {personal ? (
          <p id={`${ids}-title-note`} role="alert" className="text-[12px] text-bad">
            {T.titlePersonal}
          </p>
        ) : (
          <p id={`${ids}-title-note`} className="text-[12px] text-ink-3">
            {T.pinHint}
          </p>
        )}
      </div>

      <DialogFooter reasonId={`${ids}-why`} reason={reason}>
        <Button onClick={ctl.requestClose}>{LABEL.cancel}</Button>
        <Button
          variant="primary"
          icon={BookmarkPlus}
          disabled={reason !== null || pin.isPending}
          aria-describedby={reason ? `${ids}-why` : undefined}
          onClick={() => pin.mutate()}
        >
          {LABEL.savePin}
        </Button>
      </DialogFooter>
    </div>
  );
}

/** Окно примера для дня `day`; `null` — закрыто. `view` — день уже посчитан (пример правила). */
export function PinGoldenDialog({
  day,
  view,
  tzOffset,
  onClose,
}: {
  day: OpenDay | null;
  view?: DayView;
  tzOffset: string;
  onClose: () => void;
}) {
  return (
    <GuardedModal
      open={day !== null}
      onClose={onClose}
      title={LABEL.pin}
      description={T.pinIntro}
      width="w-[520px]"
    >
      {(ctl) =>
        day && (
          <PinBody
            key={`${day.personId}|${day.date}`}
            day={day}
            view={view}
            tzOffset={tzOffset}
            ctl={ctl}
            onDone={() => {
              ctl.setDirty(false);
              onClose();
            }}
          />
        )
      }
    </GuardedModal>
  );
}
